import { WORKSHIFT } from '@hive/status'
import React, { useCallback } from 'react'
import { Table, Tag, Pagination, IconButton, Panel } from 'rsuite'
import type { SortType } from 'rsuite/Table'
import EditIcon from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import EyeIcon from '@rsuite/icons/EyeRound'
import AppLoader from '@components/common/AppLoader'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { CourierWorkShift } from '@@partner:types:app'
import { formatDate } from '@@partner:helpers/util'
import type { GridSort, PageInfo } from '@@partner:types:app'
import { formatWorkshiftStatusLabel } from '@@partner:helpers/formatters/workshift'

const { Column, HeaderCell, Cell } = Table

export interface ShiftsTableProps {
  data: CourierWorkShift[]
  loading: boolean
  error?: any
  pageInfo?: PageInfo
  tableSort: GridSort
  page: number
  onSortColumn: (column: string, sortType?: SortType) => void
  onPageSelect: (page: number) => void
  onEditShift: (shift: CourierWorkShift) => void
  onDeleteShift: (shift: CourierWorkShift) => void
  onViewShift: (shift: CourierWorkShift) => void
}

export default function ShiftsTable ({
  data,
  loading,
  error,
  pageInfo,
  tableSort,
  page,
  onSortColumn,
  onPageSelect,
  onEditShift,
  onDeleteShift,
  onViewShift
}: ShiftsTableProps) {

  // Форматирование данных для отображения
  const formatShiftTime = useCallback((plannedStart: string | number, plannedFinish: string | number) => {
    const startDate = new Date(Number(plannedStart))
    const finishDate = new Date(Number(plannedFinish))
    
    // Проверяем, что даты валидны
    if (isNaN(startDate.getTime()) || isNaN(finishDate.getTime())) {
      return 'Некорректное время'
    }
    
    const startTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`
    const finishTime = `${finishDate.getHours().toString().padStart(2, '0')}:${finishDate.getMinutes().toString().padStart(2, '0')}`
    
    return `${startTime} - ${finishTime}`
  }, [])

  const formatShiftDate = useCallback((plannedStart: string | number) => {
    const date = new Date(Number(plannedStart))
    
    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) {
      return 'Некорректная дата'
    }
    
    return formatDate(date, false)
  }, [])

  const formatActualTime = useCallback((rowData: CourierWorkShift) => {
    if (rowData.startedAt && rowData.finishedAt) {
      const start = new Date(Number(rowData.startedAt))
      const finish = new Date(Number(rowData.finishedAt))
      
      // Проверяем, что даты валидны
      if (isNaN(start.getTime()) || isNaN(finish.getTime())) {
        return 'Некорректное время'
      }
      
      return `${start.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - ${finish.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
    }
    else if (rowData.startedAt) {
      const start = new Date(Number(rowData.startedAt))
      
      // Проверяем, что дата валидна
      if (isNaN(start.getTime())) {
        return 'Некорректное время'
      }
      
      return `${start.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - В процессе`
    }
    return 'Не начата'
  }, [])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки смен"
      />
    )
  }

  return (
    <Panel header="Смены курьеров" bordered>
      <Table
        data={data}
        autoHeight
        wordWrap
        loading={loading}
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={onSortColumn}
        renderLoading={() =>
          loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
        }
        affixHeader
        rowKey="id"
      >
        <Column sortable flexGrow={1} fixed verticalAlign="middle">
          <HeaderCell>Дата</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => formatShiftDate(rowData.plannedStartAt)}
          </Cell>
        </Column>

        <Column sortable flexGrow={1} verticalAlign="middle">
          <HeaderCell>Время смены</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => formatShiftTime(rowData.plannedStartAt, rowData.plannedFinishAt)}
          </Cell>
        </Column>

        <Column sortable flexGrow={3} verticalAlign="middle">
          <HeaderCell>Курьер</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => 
              rowData.courier ? `${rowData.courier.first_name} ${rowData.courier.last_name}` : 'Не назначен'
            }
          </Cell>
        </Column>

        <Column verticalAlign="middle" flexGrow={1}>
          <HeaderCell>Транспорт</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => {
              const vehicleInfo = rowData.vehicle

              const modelInfo = [ vehicleInfo?.brand, vehicleInfo?.model ]
              const stringToShow = modelInfo.filter(Boolean).join(' ')
              return vehicleInfo ? 
                `${stringToShow} ${vehicleInfo.license_plate ? '(' + vehicleInfo.license_plate + ')' : ''}` : 
                'Не назначен'
            }}
          </Cell>
        </Column>

        <Column sortable verticalAlign="middle">
          <HeaderCell>Статус</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => {
              const statusConfig = formatWorkshiftStatusLabel(rowData.status)
              return (
                <Tag color={statusConfig?.color || undefined}>
                  {statusConfig?.label || 'Неизвестно'}
                </Tag>
              )
            }}
          </Cell>
        </Column>

        <Column verticalAlign="middle" flexGrow={2}>
          <HeaderCell>Фактическое время</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => formatActualTime(rowData)}
          </Cell>
        </Column>

        <Column verticalAlign="middle">
          <HeaderCell>Комментарий</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => rowData.comment || '—'}
          </Cell>
        </Column>

        <Column fixed="right" verticalAlign="middle" flexGrow={2}>
          <HeaderCell>Управление</HeaderCell>
          <Cell>
            {(rowData: CourierWorkShift) => (
              <div style={{ display: 'flex', gap: '8px' }}>
                <IconButton
                  appearance="subtle"
                  size="sm"
                  icon={<EyeIcon />}
                  onClick={() => onViewShift(rowData)}
                  style={{ color: '#6c757d' }}
                  title="Просмотр смены"
                />
                <IconButton
                  appearance="subtle"
                  size="sm"
                  icon={<EditIcon />}
                  onClick={() => onEditShift(rowData)}
                  style={{ color: '#3498db' }}
                  title="Редактировать"
                />
                <IconButton
                  appearance="subtle"
                  size="sm"
                  icon={<TrashIcon />}
                  onClick={() => onDeleteShift(rowData)}
                  style={{ color: '#e74c3c' }}
                  title="Удалить"
                />
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={pageInfo?.lastPage}
        maxButtons={10}
        total={pageInfo?.total ?? 0}
        onSelect={(eventKey) => onPageSelect(Number(eventKey))}
      />
    </Panel>
  )
}
